import { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  Modal,
  TextField,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import { fadeInUp, fadeInDown, modalScaleIn } from "../../utils/animations";
import AuthContext from "../../context/AuthContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "90vw", md: 520 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
};

const emptyForm = { exigence: "", reference: "", page: "" };

function AnalyseTexte() {
  const { texteId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const userRole = user?.profile?.role || user?.role;
  const isAdmin = userRole === "ADMIN_GLOBAL";

  const [texte, setTexte] = useState(null);
  const [exigences, setExigences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  const showSnack = (message, severity = "success") => setSnack({ open: true, message, severity });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [texteRes, exigencesRes] = await Promise.all([
        api.get(`/api/textes/${texteId}`),
        api.get(`/api/exigences/texte/${texteId}`),
      ]);
      setTexte(texteRes.data);
      setExigences(Array.isArray(exigencesRes.data) ? exigencesRes.data : []);
    } catch {
      showSnack("Erreur lors du chargement des données.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [texteId]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (exigence) => {
    setEditing(exigence);
    setForm({
      exigence: exigence.exigence,
      reference: exigence.reference || "",
      page: exigence.page || "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.exigence.trim()) {
      showSnack("Le champ Exigence est obligatoire.", "warning");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/exigences/${editing.id}`, form);
        showSnack("Exigence mise à jour.");
      } else {
        await api.post("/api/exigences/", { ...form, texte_id: parseInt(texteId) });
        showSnack("Exigence ajoutée.");
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      showSnack(err?.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      message: "Supprimer cette exigence ?",
      onConfirm: async () => {
        try {
          await api.delete(`/api/exigences/${id}`);
          showSnack("Exigence supprimée.");
          fetchData();
        } catch {
          showSnack("Erreur lors de la suppression.", "error");
        }
      },
    });
  };

  const CellIndex = ({ row }) => (
    <MDTypography variant="caption" color="text">
      {row.index + 1}
    </MDTypography>
  );
  CellIndex.propTypes = { row: PropTypes.shape({ index: PropTypes.number }) };
  // eslint-disable-next-line react/prop-types
  const CellText = ({ value }) => (
    <MDTypography variant="caption" color="text" sx={{ whiteSpace: "pre-wrap" }}>
      {value}
    </MDTypography>
  );
  // eslint-disable-next-line react/prop-types
  const CellOptional = ({ value }) => (
    <MDTypography variant="caption" color="text">
      {value || "-"}
    </MDTypography>
  );
  const CellActions = ({ row }) => (
    <MDBox display="flex" gap={0.5}>
      <Tooltip title="Modifier">
        <IconButton size="small" onClick={() => openEdit(row.original)}>
          <Icon sx={{ fontSize: 18, color: "info.main" }}>edit</Icon>
        </IconButton>
      </Tooltip>
      <Tooltip title="Supprimer">
        <IconButton size="small" onClick={() => handleDelete(row.original.id)}>
          <Icon sx={{ fontSize: 18, color: "error.main" }}>delete</Icon>
        </IconButton>
      </Tooltip>
    </MDBox>
  );
  CellActions.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
    }),
  };

  const columns = [
    { Header: "#", accessor: "id", width: "5%", Cell: CellIndex },
    { Header: "Exigence", accessor: "exigence", Cell: CellText, wrap: true },
    { Header: "Référence", accessor: "reference", width: "15%", Cell: CellOptional },
    { Header: "Page", accessor: "page", width: "10%", Cell: CellOptional },
  ];

  if (isAdmin) {
    columns.push({ Header: "Actions", accessor: "actions", width: "12%", Cell: CellActions });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <MDBox mb={2} sx={{ animation: `${fadeInDown} 0.4s ease-out both` }}>
              <MDButton
                variant="text"
                color="info"
                size="small"
                onClick={() => navigate(-1)}
                sx={{ pl: 0 }}
              >
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                Retour
              </MDButton>
            </MDBox>

            <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
              {/* En-tête violet */}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="purple"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
              >
                <MDBox>
                  <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                    Analyse du texte
                  </MDTypography>
                  {texte && (
                    <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      {texte.titre} - <em>{texte.reference}</em>
                    </MDTypography>
                  )}
                </MDBox>
                {isAdmin && (
                  <MDButton variant="outlined" color="white" size="small" onClick={openCreate}>
                    <Icon sx={{ mr: 0.5 }}>add</Icon>
                    Ajouter une exigence
                  </MDButton>
                )}
              </MDBox>

              {/* Tableau */}
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : exigences.length === 0 ? (
                  <MDBox p={4} textAlign="center">
                    <Icon sx={{ fontSize: 48, color: "grey.400", mb: 1 }}>playlist_add_check</Icon>
                    <MDTypography variant="h6" color="text">
                      Aucune exigence enregistrée pour ce texte.
                    </MDTypography>
                    {isAdmin && (
                      <MDTypography variant="body2" color="text" mt={1}>
                        Cliquez sur &quot;Ajouter une exigence&quot; pour commencer l&apos;analyse.
                      </MDTypography>
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows: exigences }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modal Ajout / Modification */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={{ ...modalStyle, animation: `${modalScaleIn} 0.25s ease-out both` }}>
          <MDTypography variant="h6" fontWeight="bold" mb={2}>
            {editing ? "Modifier l'exigence" : "Ajouter une exigence"}
          </MDTypography>

          <TextField
            label="Exigence *"
            fullWidth
            multiline
            minRows={3}
            value={form.exigence}
            onChange={(e) => setForm({ ...form, exigence: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 1000 }}
            helperText={`${form.exigence.length}/1000`}
          />
          <TextField
            label="Référence (optionnel)"
            fullWidth
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="Ex: Art. 12, Section 3..."
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Page (optionnel)"
            fullWidth
            value={form.page}
            onChange={(e) => setForm({ ...form, page: e.target.value })}
            sx={{ mb: 3 }}
            placeholder="Ex: 15"
            inputProps={{ maxLength: 50 }}
          />

          <MDBox display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="outlined" color="secondary" onClick={() => setModalOpen(false)}>
              Annuler
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
              {saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : editing ? (
                "Enregistrer"
              ) : (
                "Ajouter"
              )}
            </MDButton>
          </MDBox>
        </Box>
      </Modal>

      {/* Dialog de confirmation */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default AnalyseTexte;
